import { useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICE_DETAILS, INSTALL_STEPS } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { QuoteForm } from "@/components/QuoteForm";
import { CtaBanner } from "@/components/CtaBanner";
import { SmartControl } from "@/components/SmartControl";

export default function ServiceDetail() {
  const { slug } = useParams();
  if (slug === "heat-pump-hot-water") return <Navigate to="/services/heat-pump-hot-water" replace />;
  const d = SERVICE_DETAILS[slug];
  if (!d) return <Navigate to="/services" replace />;

  return (
    <>
      <PageHero
        label={d.tagline}
        title={d.title}
        subtitle={d.intro}
        image={d.hero}
        crumbs={[{ label: "Services", to: "/services" }, { label: d.title }]}
      />

      {/* What is it + suits */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>Overview</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              What is {d.title.toLowerCase()}?
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{d.what}</p>

            <h3 className="mt-10 font-display text-xl font-semibold text-brand-blue">Who it suits</h3>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {d.suits.map((s) => (
                <li key={s} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-electric/10">
                    <Check className="h-3.5 w-3.5 text-brand-electric" />
                  </span>
                  <span className="text-[15px]">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid gap-4">
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <img src={d.images[0]} alt={`${d.title} installation`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <img src={d.images[1]} alt={`${d.title} project`} loading="lazy" className="aspect-[16/9] w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits + Why choose */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Benefits</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Why homeowners choose this system
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {d.benefits.map((b, i) => (
              <Reveal key={b} delay={(i % 2) * 0.08}>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold/15">
                    <Check className="h-5 w-5 text-brand-gold" />
                  </span>
                  <p className="text-[15px] font-medium text-brand-blue">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {d.services && (
            <Reveal>
              <div className="mt-12 rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
                <h3 className="font-display text-2xl font-semibold text-brand-blue">Our electrical services include</h3>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {d.services.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-slate-700">
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-electric" />
                      <span className="text-[15px]">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Our Process</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              A simple, four-step installation
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {INSTALL_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl border border-slate-200 bg-[#FAFAFA] p-7">
                  <div className="font-display text-4xl font-bold text-brand-electric/20">{s.n}</div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-brand-blue">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {d.showAirTouch && <SmartControl />}

      {/* Quote */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>Request a Quote</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-blue">
              Get a free quote for {d.title.toLowerCase()}
            </h2>
            <p className="mt-5 max-w-md text-lg text-slate-600 leading-relaxed">
              Tell us about your property and we'll recommend the right solution with honest, upfront pricing.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
              <QuoteForm defaultService={d.title.includes("Ducted") ? "Ducted Air Conditioning" : d.title.includes("Multi") ? "Multi-Split Air Conditioning" : d.title.includes("Split") ? "Split System Air Conditioning" : d.title.includes("Electrical") ? "Electrical Services" : ""} />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
